import React, { createContext, useContext, useState, useCallback } from 'react';
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';

const ModalContext = createContext(null);

export function ModalProvider({ children }) {
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: 'alert', // 'alert' | 'confirm'
    message: '',
    onConfirm: null,
    onCancel: null,
  });

  const showAlert = useCallback((message) => {
    setModalState({
      isOpen: true,
      type: 'alert',
      message,
      onConfirm: () => setModalState(prev => ({ ...prev, isOpen: false })),
      onCancel: null,
    });
  }, []);

  const showConfirm = useCallback((message, onConfirmCallback, onCancelCallback = null) => {
    setModalState({
      isOpen: true,
      type: 'confirm',
      message,
      onConfirm: () => {
        setModalState(prev => ({ ...prev, isOpen: false }));
        if (onConfirmCallback) onConfirmCallback();
      },
      onCancel: () => {
        setModalState(prev => ({ ...prev, isOpen: false }));
        if (onCancelCallback) onCancelCallback();
      },
    });
  }, []);

  return (
    <ModalContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      {modalState.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-[#111] border border-white/10 rounded-xl p-6 w-full max-w-sm sm:max-w-md shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex flex-col items-center text-center space-y-4">
              {modalState.type === 'confirm' ? (
                <AlertCircle className="w-12 h-12 text-yellow-500" />
              ) : (
                <CheckCircle className="w-12 h-12 text-green-500" />
              )}
              
              <p className="text-white text-lg font-medium">
                {modalState.message}
              </p>
              
              <div className="flex w-full gap-3 mt-6 pt-2">
                {modalState.type === 'confirm' && (
                  <button
                    onClick={modalState.onCancel}
                    className="flex-1 px-4 py-2 bg-transparent border border-white/20 text-white rounded-lg hover:bg-white/5 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20"
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={modalState.onConfirm}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium text-black transition-colors focus:outline-none focus:ring-2 ${
                    modalState.type === 'confirm' 
                      ? 'bg-red-500 hover:bg-red-600 focus:ring-red-500/50 text-white' 
                      : 'bg-silver hover:bg-white focus:ring-silver/50'
                  }`}
                >
                  {modalState.type === 'confirm' ? 'Confirm' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
